import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { cities } from '@/data/cities';
import logoSrc from '@/assets/logo-body-by-cc.webp';
import logoBtoSrc from '@/assets/logo-bto-blanc.webp';

const SiteFooter = () => {
  return (
    <footer className="bg-petrol text-petrol-foreground">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Logo & description */}
          <div className="flex flex-col items-start">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-3">
              <img src={logoSrc} alt="Body by CC" className="h-28 w-auto" width={112} height={112} loading="lazy" />
              <div>
                <span className="font-heading font-semibold text-lg text-petrol-foreground block">Body by CC</span>
                <span className="text-sm text-petrol-foreground/80 font-body block">Institut Santé & Esthétique</span>
              </div>
            </Link>
            <p className="text-sm text-petrol-foreground/85 mt-5 max-w-xs leading-relaxed font-body">
              Institut de beauté et bien-être à Sevran. Soins du visage, du corps et esthétique médicale dans un cadre chaleureux et bienveillant.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/75 mb-5" role="heading" aria-level={2}>Navigation</p>
            <nav className="flex flex-col gap-3">
              {[
                { label: 'Accueil', path: '/' },
                { label: 'Nos soins', path: '/soins' },
                { label: 'Formules', path: '/formules' },
                { label: 'Offrir', path: '/offrir' },
                { label: 'À propos', path: '/a-propos' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Blog', path: '/blog' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className="text-sm text-petrol-foreground/70 hover:text-petrol-foreground transition-colors font-body"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Villes proches */}
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/75 mb-5" role="heading" aria-level={2}>Villes proches</p>
            <nav className="flex flex-col gap-3">
              {cities.slice(0, 8).map((city) => (
                <Link
                  key={city.slug}
                  to={`/ville/${city.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="text-sm text-petrol-foreground/70 hover:text-petrol-foreground transition-colors font-body"
                >
                  Body by CC {city.prefix} {city.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/75 mb-5" role="heading" aria-level={2}>Contact</p>
            <div className="flex flex-col gap-4 text-sm text-petrol-foreground/70 font-body">
              <a href="https://maps.app.goo.gl/MYjhdAwtngvYopSZ9" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:text-petrol-foreground transition-colors">
                <MapPin size={15} className="text-petrol-foreground/75 shrink-0 mt-0.5" />
                65 Bd Westinghouse, 93270 Sevran
              </a>
              <a href="mailto:kreent@yahoo.fr" className="flex items-center gap-3 hover:text-petrol-foreground transition-colors">
                <Mail size={15} className="text-petrol-foreground/75 shrink-0" />
                kreent@yahoo.fr
              </a>
              <a href="tel:+33645230034" className="flex items-center gap-3 hover:text-petrol-foreground transition-colors">
                <Phone size={15} className="text-petrol-foreground/75 shrink-0" />
                06 45 23 00 34
              </a>
            </div>
            <p className="text-xs text-petrol-foreground/75 mt-5 leading-relaxed font-body">
              Lundi : Fermé<br />
              Mar-Dim : 10h-20h
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-petrol-foreground/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col items-center gap-3 md:flex-row md:justify-between md:gap-4">
          <p className="text-xs text-petrol-foreground/75 font-body">
            © {new Date().getFullYear()} Body by CC — Tous droits réservés.
          </p>
          <div className="flex items-center gap-6 text-xs text-petrol-foreground/75 font-body">
            <Link to="/mentions-legales" onClick={() => window.scrollTo(0, 0)} className="hover:text-petrol-foreground transition-colors">Mentions légales</Link>
            <Link to="/conditions-generales-de-vente" onClick={() => window.scrollTo(0, 0)} className="hover:text-petrol-foreground transition-colors">CGV</Link>
            <Link to="/politique-de-confidentialite" onClick={() => window.scrollTo(0, 0)} className="hover:text-petrol-foreground transition-colors">Confidentialité</Link>
          </div>
          <span className="text-xs text-petrol-foreground/75 flex items-center gap-2 font-body">
            Site réalisé par
            <a href="https://b-to.io/" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img src={logoBtoSrc} alt="B-to" className="h-6 w-auto inline-block" width={24} height={24} loading="lazy" />
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};
export default SiteFooter;